import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ImageWithBasePath from "../../../core/common/imageWithBasePath";
import { useLogin } from "./hooks/useLogin";
import { Form } from "react-bootstrap";
import { LoginFormData } from "./types/loginTypes";
import { showToast } from "../../../core/utils/alert";
// import { LoginApiResponse } from "../../../core/data/rtk/baseQuery";
import { setCredentials } from "../../../core/data/slice/authSlice";
import { useAppDispatch } from "../../../core/data/redux/store";

const Login = () => {
  const routes = all_routes;
  const navigation = useNavigate();
  const {
    passwordVisibility,
    togglePasswordVisibility,
    handleLogin,
    isLoading,
    // error,
  } = useLogin();
  const [formData, setFormData] = useState<LoginFormData>({
    username: "",
    password: "",
  });
  const [rememberMe, setRememberMe] = useState<boolean>(false);
  const dispatch = useAppDispatch();

  useEffect(() => {
    // Check localStorage for saved username and password
    const savedUsername = localStorage.getItem("username");
    const savedPassword = localStorage.getItem("password");

    if (savedUsername) {
      setFormData((prev) => ({ ...prev, username: savedUsername }));
      setRememberMe(true); // Check the Remember Me checkbox if username exists
    }
    if (savedPassword) {
      setFormData((prev) => ({ ...prev, password: savedPassword }));
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Trim the username to remove whitespace
    const trimmedUsername = formData.username.trim();
    const trimmedPassword = formData.password.trim();

    // Check if the trimmed username and password is empty
    if (trimmedUsername === "") {
      showToast({
        title: "Error",
        text: "Username cannot be empty.",
        icon: "error",
        timer: 5000,
      });
      return;
    }

    if (trimmedPassword === "") {
      showToast({
        title: "Error",
        text: "Password cannot be empty.",
        icon: "error",
        timer: 5000,
      });
      return;
    }

    try {
      const response = await handleLogin({
        ...formData,
        username: trimmedUsername,
      });
      if (response?.success) {
        dispatch(setCredentials(response.data));
        navigation(routes.adminDashboard);

        // Store username and password in localStorage if Remember Me is checked
        if (rememberMe) {
          localStorage.setItem("username", trimmedUsername);
          localStorage.setItem("password", formData.password);
        } else {
          localStorage.removeItem("username");
          localStorage.removeItem("password");
        }

        // Save username and password hash for lock screen in session storage
        sessionStorage.setItem("lockScreenUsername", trimmedUsername);
        // Use simple encoding for demo - in production use a better hashing method
        sessionStorage.setItem(
          "lockScreenPasswordHash",
          btoa(formData.password)
        );

        showToast({
          title: "Success",
          text: response.message || "Login successful!",
          icon: "success",
          timer: 5000,
        });
      } else {
        showToast({
          title: "Error",
          text: response.message || "Invalid Credentials!",
          icon: "error",
          timer: 5000,
        });
      }
    } catch (error) {
      // const apiError = error as LoginApiResponse;
      showToast({
        title: "Error",
        // text: apiError.message || apiError.error || "Login failed",
        text: "Invalid Credentials!",
        icon: "error",
        timer: 5000,
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  return (
    <>
      <div className="container-fuild account-page">
        <div className="login-wrapper w-100 overflow-hidden position-relative flex-wrap d-block vh-100">
          <div className="row">
            <div className="col-lg-6">
              <div className="d-lg-flex align-items-center justify-content-center bg-light-300 d-lg-block d-none flex-wrap vh-100 overflowy-auto bg-01">
                <div>
                  <ImageWithBasePath
                    src="assets/img/authentication/authentication-11.svg"
                    alt="Img"
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-6 col-md-12 col-sm-12">
              <div className="row justify-content-center align-items-center vh-100 overflow-auto flex-wrap ">
                <div className="col-md-8 mx-auto p-2">
                  <form onSubmit={handleSubmit}>
                    <div>
                      <div
                        className=" mx-auto mb-5 text-center"
                        style={{ width: "250px" }}
                      >
                        <ImageWithBasePath
                          src="assets/img/authentication/authentication-logo.png"
                          className="img-fluid"
                          alt="Logo"
                        />
                      </div>
                      <div className="card">
                        <div className="card-body p-4">
                          <div className=" mb-4">
                            <h2 className="mb-2">Welcome</h2>
                            <p className="mb-0">
                              Please enter your details to sign in
                            </p>
                          </div>
                          <div className="mb-3 ">
                            <Form.Group controlId="username">
                              <Form.Label>Username</Form.Label>
                              <div className="input-icon position-relative">
                                <span className="input-icon-addon">
                                  <i className="ti ti-user" />
                                </span>
                                <Form.Control
                                  type="text"
                                  name="username"
                                  value={formData.username}
                                  onChange={handleInputChange}
                                  required
                                  placeholder="Enter your username"
                                />
                                <Form.Control.Feedback type="invalid">
                                  Please enter a username
                                </Form.Control.Feedback>
                              </div>
                            </Form.Group>

                            <Form.Group controlId="password" className="mt-3">
                              <Form.Label>Password</Form.Label>
                              <div className="pass-group">
                                <Form.Control
                                  type={
                                    passwordVisibility ? "text" : "password"
                                  }
                                  name="password"
                                  value={formData.password}
                                  onChange={handleInputChange}
                                  required
                                  placeholder="Enter your password"
                                />
                                <Form.Control.Feedback type="invalid">
                                  Please enter a password
                                </Form.Control.Feedback>
                                <span
                                  className={`ti toggle-passwords ${
                                    passwordVisibility ? "ti-eye" : "ti-eye-off"
                                  }`}
                                  onClick={() =>
                                    togglePasswordVisibility("password")
                                  }
                                ></span>
                              </div>
                            </Form.Group>
                          </div>
                          <div className="form-wrap form-wrap-checkbox mb-3">
                            <div className="d-flex align-items-center">
                              <div className="form-check form-check-md mb-0">
                                <input
                                  className="form-check-input mt-0"
                                  type="checkbox"
                                  checked={rememberMe}
                                  onChange={() => setRememberMe(!rememberMe)}
                                />
                              </div>
                              <p className="ms-1 mb-0">Remember Me</p>
                            </div>
                            <div className="text-end ">
                              <Link
                                to={routes.forgotPassword}
                                className="link-danger"
                              >
                                Forgot Password?
                              </Link>
                            </div>
                          </div>
                          <div className="mb-3">
                            <button
                              type="submit"
                              className="btn btn-primary w-100"
                              disabled={isLoading}
                            >
                              {isLoading ? "Signing In..." : "Sign In"}
                            </button>
                          </div>
                        </div>
                      </div>
                      <div className="mt-5 text-center">
                        <p className="mb-0 ">
                          Copyright © {new Date().getFullYear()} - Aloha
                        </p>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Login;
